import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, Send, MessageCircle } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "",
    message: "",
    privacyConsent: false,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // NOTE for Backend: 'formData.topic' must be shown at the top of the received email/record
      // e.g. "New request: [formData.topic]"
      
      await new Promise((resolve) => setTimeout(resolve, 1500));
      
      setIsSuccess(true);
      setFormData({ name: "", email: "", phone: "", topic: "", message: "", privacyConsent: false });
    } catch (error) {
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>  
      <Navbar/>
      <section className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
              Get In Touch
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Want to speak with a doctor or pharmacist, ask about a medicine or check on an order? Our team is here to help you 24/7.
            </p>
          </motion.div>

          {/* Contact Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            
            {/* Left Side - Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-lg p-8 space-y-6"
            >
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                Contact Information
              </h2>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center shrink-0">
                  <Mail className="text-primary w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Email</p>
                  <a href="mailto:prosemedistore@gmail.com" className="text-gray-600 hover:text-primary transition-colors">
                    prosemedistore@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center shrink-0">
                  <Phone className="text-primary w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">General inquiries (calls)</p>
                  <a href="tel:+2349072096805" className="text-gray-600 hover:text-primary transition-colors">
                    +234 907 209 6805
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center shrink-0">
                  <MessageCircle className="text-green-600 w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Consultations (WhatsApp)</p>
                  <a 
                    href="https://wa.me/2348162738527" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-green-700 font-medium hover:underline transition-colors"
                  >
                    +234 816 273 8527
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center shrink-0">
                  <MapPin className="text-primary w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Address</p>
                  <p className="text-gray-600">
                    Administrative Office: 3C7J+8PW, Utako, Abuja, Nigeria.
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <p className="text-gray-600 text-sm leading-relaxed">
                  Chat with a doctor or pharmacist on <a href="https://wa.me/2348162738527" target="_blank" rel="noopener noreferrer" className="text-green-600 font-semibold hover:underline">WhatsApp</a> for real-time consultation.
                </p>
              </div>
            </motion.div>

            {/* Right Side - Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-lg relative overflow-hidden"
            >
              <AnimatePresence mode="wait">
                {!isSuccess ? (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={handleSubmit}
                    className="p-8 space-y-5"
                  >
                    <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                      Send Us a Message
                    </h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="you@example.com"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone number (WhatsApp)
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="e.g. 0803 000 0000"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        What do you need help with? <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="topic"
                        value={formData.topic}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none bg-white transition-all"
                      >
                        <option value="" disabled>Choose one ▼</option>
                        <option value="Speak with a doctor">Speak with a doctor</option>
                        <option value="Speak with a pharmacist">Speak with a pharmacist</option>
                        <option value="Order or delivery">Order or delivery</option>
                        <option value="General inquiry">General inquiry</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="4"
                        placeholder="How can we help you?"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none transition-all resize-y"
                      ></textarea>
                    </div>

                    <div className="flex items-start gap-3 py-2">
                      <input
                        type="checkbox"
                        name="privacyConsent"
                        id="privacyConsent"
                        checked={formData.privacyConsent}
                        onChange={handleChange}
                        required
                        className="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                      />
                      <label htmlFor="privacyConsent" className="text-sm text-gray-600 leading-snug cursor-pointer">
                        I agree to ProseMediStore's <a href="/privacy" className="text-primary hover:underline font-medium">Privacy Policy</a> and consent to be contacted about my request.
                      </label>
                    </div>

                    <div className="pt-2">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg font-semibold text-white shadow-sm ${
                          isSubmitting
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-primary hover:bg-primary/95"
                        } transition-all`}
                      >
                        <Send size={18} />
                        {isSubmitting ? "Sending..." : "Send Message"}
                      </motion.button>
                      
                      {/* Emergency Warning */}
                      <div className="mt-5 border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                        <p className="text-sm text-red-900 leading-relaxed">
                          <strong className="font-bold">NEW:</strong> For medical emergencies, do not wait for a reply. Go to the nearest hospital immediately.
                        </p>
                      </div>
                    </div>
                  </motion.form>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-10 text-center flex flex-col items-center justify-center h-full min-h-[500px]"
                  >
                    <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
                      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-3">Thank you.</h3>
                    <p className="text-gray-600 mb-8 max-w-sm text-lg">
                      Our team will get back to you shortly. For consultations, chat with us on WhatsApp.
                    </p>
                    <a 
                      href="https://wa.me/2348162738527" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-3.5 rounded-xl font-semibold shadow hover:bg-[#20bd5a] hover:shadow-md transition-all w-full sm:w-auto"
                    >
                      <MessageCircle size={20} />
                      Chat on WhatsApp
                    </a>
                    <button 
                      onClick={() => setIsSuccess(false)} 
                      className="mt-8 text-sm text-gray-500 hover:text-primary font-medium transition-colors"
                    >
                      Send another message
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </section>
      <Footer/>
    </>
  );
}